import React, { useState, useEffect } from 'react';
import { FaLanguage, FaChartBar, FaGlobe, FaCheckCircle, FaClock, FaExclamationTriangle, FaTimes } from 'react-icons/fa';
import { fetchTranslateLogDashboard, fetchTranslateLogAnalytics, fetchTranslateLogs } from '../../services/service';
import { useAsgardeoAuth } from '../../hooks/useAsgardeoAuth';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const TranslateDashboard = () => {
  const [stats, setStats] = useState({
    totalTranslations: 0,
    completedTranslations: 0,
    pendingTranslations: 0,
    failedTranslations: 0,
    averageConfidence: 0,
    topLanguages: [],
    recentActivity: [],
    // New stats for enhanced dashboard
    recent7DaysCount: 0,
    monthlyCount: 0,
    languagePairStats: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analytics, setAnalytics] = useState({
    totalTranslates: 0,
    totalSuccess: 0,
    totalFailed: 0,
    perDay: []
  });
  const [chartData, setChartData] = useState({
    dailyChart: null,
    hourlyFailedChart: null
  });

  const auth = useAsgardeoAuth();

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load dashboard stats
      const dashboardData = await fetchTranslateLogDashboard(auth);
      console.log('dashboardData:', dashboardData.monthlyCount);
      
      // Load analytics data
      const analyticsData = await fetchTranslateLogAnalytics(auth);

      // Fetch ALL translate logs to calculate statistics from total data
      const allLogsResponse = await fetchTranslateLogs({ 
        page: 1, 
        limit: 10000, // Fetch a large number to get all logs
        authContext: auth 
      });

      const allLogs = allLogsResponse.logs || [];
      console.log('Total logs fetched:', allLogs.length);

      // Helper function to parse language pair
      const parseLanguagePair = (sourceToTarget) => {
        if (!sourceToTarget) return { source: 'Unknown', target: 'Unknown' };
        
        const parts = sourceToTarget.split('_');
        if (parts.length >= 2) {
          const languageMap = {
            'en': 'English',
            'ta': 'Tamil',
            'si': 'Sinhala',
            'fr': 'French',
            'de': 'German',
            'es': 'Spanish',
            'it': 'Italian',
            'pt': 'Portuguese',
            'ru': 'Russian',
            'ja': 'Japanese',
            'ko': 'Korean',
            'zh': 'Chinese',
            'ar': 'Arabic',
            'hi': 'Hindi'
          };
          
          return {
            source: languageMap[parts[0]] || parts[0].toUpperCase(),
            target: languageMap[parts[1]] || parts[1].toUpperCase()
          };
        }
        
        return { source: 'Unknown', target: 'Unknown' };
      };

      // Calculate statistics from ALL logs
      const now = new Date();
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      // Calculate recent 7 days count from total logs
      const recent7DaysCount = allLogs.filter(log => {
        const logDate = new Date(log.timestamp);
        return logDate >= sevenDaysAgo;
      }).length;

      // Calculate monthly count from total logs
      const monthlyCount = allLogs.filter(log => {
        const logDate = new Date(log.timestamp);
        return logDate >= thirtyDaysAgo;
      }).length;

      // Calculate language pair statistics from total logs
      const languagePairCounts = {};
      
      allLogs.forEach(log => {
        const languagePair = parseLanguagePair(log.source_to_target);
        const pairKey = `${languagePair.source} → ${languagePair.target}`;
        
        if (!languagePairCounts[pairKey]) {
          languagePairCounts[pairKey] = {
            pair: pairKey,
            source: languagePair.source,
            target: languagePair.target,
            count: 0,
            success: 0,
            failed: 0
          };
        }
        
        languagePairCounts[pairKey].count++;
        
        if (log.status && ['success', 'completed'].includes(log.status.toLowerCase())) {
          languagePairCounts[pairKey].success++;
        } else if (log.status && ['failed', 'error'].includes(log.status.toLowerCase())) {
          languagePairCounts[pairKey].failed++;
        }
      });
      
      // Convert to array and sort by count
      const languagePairStats = Object.values(languagePairCounts)
        .sort((a, b) => b.count - a.count)
        .slice(0, 10); // Top 10 language pairs

      // Process dashboard data
      const processedStats = {
        totalTranslations: dashboardData.todayCount || 0,
        completedTranslations: dashboardData.todaySuccess || 0,
        pendingTranslations: 0, // Calculate from logs if available
        failedTranslations: (dashboardData.todayCount || 0) - (dashboardData.todaySuccess || 0),
        averageConfidence: 0.92, // Default value, could be calculated from logs
        topLanguages: [], // Could be calculated from logs
        recentActivity: dashboardData.logs?.slice(0, 5).map(log => {
          const languagePair = parseLanguagePair(log.source_to_target);
          return {
            id: log._id || log.id,
            source: languagePair.source,
            target: languagePair.target,
            text: log.input_text || log.originalText || log.original_text || log.text || 'N/A',
            status: log.status || 'unknown',
            time: new Date(log.timestamp).toLocaleString()
          };
        }) || [],
        // Use calculated statistics from total logs
        recent7DaysCount: recent7DaysCount,
        monthlyCount: monthlyCount,
        languagePairStats: languagePairStats
      };
      console.log('Processed Stats from total logs:', processedStats);

      // Calculate pending translations from logs
      if (dashboardData.logs) {
        processedStats.pendingTranslations = dashboardData.logs.filter(
          log => log.status && !['success', 'completed', 'failed', 'error'].includes(log.status.toLowerCase())
        ).length;
      }

      // Process analytics data
      setAnalytics({
        totalTranslates: analyticsData.totalTranslates || 0,
        totalSuccess: analyticsData.totalSuccess || 0,
        totalFailed: analyticsData.totalFailed || 0,
        perDay: analyticsData.perDay || []
      });

      // Generate chart data
      const chartData = generateChartData(allLogs);
      setChartData(chartData);

      setStats(processedStats);
    } catch (err) {
      console.error('Error loading translate dashboard data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'success':
        return <FaCheckCircle className="text-green-500" />;
      case 'pending':
      case 'processing':
        return <FaClock className="text-yellow-500" />;
      case 'failed':
      case 'error':
        return <FaExclamationTriangle className="text-red-500" />;
      default:
        return <FaClock className="text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'success':
        return 'text-green-600 bg-green-100';
      case 'pending':
      case 'processing':
        return 'text-yellow-600 bg-yellow-100';
      case 'failed':
      case 'error':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const generateChartData = (logs) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Daily chart data for last 7 days
    const dailyData = [];
    const dailyLabels = [];
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      dailyLabels.push(dateStr);
      
      const dayLogs = logs.filter(log => {
        const logDate = new Date(log.timestamp);
        return logDate.toDateString() === date.toDateString();
      });
      
      dailyData.push(dayLogs.length);
    }
    
    // 7-day bar chart data
    const sevenDayData = [];
    const sevenDayLabels = [];
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      sevenDayLabels.push(dateStr);
      
      const dayLogs = logs.filter(log => {
        const logDate = new Date(log.timestamp);
        return logDate.toDateString() === date.toDateString();
      });
      
      sevenDayData.push(dayLogs.length);
    }
    
    // 30-day bar chart data
    const thirtyDayData = [];
    const thirtyDayLabels = [];
    
    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      thirtyDayLabels.push(dateStr);
      
      const dayLogs = logs.filter(log => {
        const logDate = new Date(log.timestamp);
        return logDate.toDateString() === date.toDateString();
      });
      
      thirtyDayData.push(dayLogs.length);
    }
    
    // Hourly failed chats data for today
    const hourlyFailedData = [];
    const hourlyLabels = [];
    
    for (let hour = 0; hour < 24; hour++) {
      const hourStr = `${hour.toString().padStart(2, '0')}:00`;
      hourlyLabels.push(hourStr);
      
      const hourLogs = logs.filter(log => {
        const logDate = new Date(log.timestamp);
        const logHour = logDate.getHours();
        const isToday = logDate.toDateString() === today.toDateString();
        const isFailed = log.status && ['failed', 'error'].includes(log.status.toLowerCase());
        
        return isToday && isFailed && logHour === hour;
      });
      
      hourlyFailedData.push(hourLogs.length);
    }
    
    return {
      dailyChart: {
        labels: dailyLabels,
        datasets: [
          {
            label: 'Translations',
            data: dailyData,
            borderColor: 'rgb(59, 130, 246)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            tension: 0.4,
            fill: true
          }
        ]
      },
      sevenDayBarChart: {
        labels: sevenDayLabels,
        datasets: [
          {
            label: 'Translations (7 Days)',
            data: sevenDayData,
            backgroundColor: 'rgba(99, 102, 241, 0.8)',
            borderColor: 'rgb(99, 102, 241)',
            borderWidth: 1,
            borderRadius: 4
          }
        ]
      },
      thirtyDayBarChart: {
        labels: thirtyDayLabels,
        datasets: [
          {
            label: 'Translations (30 Days)',
            data: thirtyDayData,
            backgroundColor: 'rgba(168, 85, 247, 0.8)',
            borderColor: 'rgb(168, 85, 247)',
            borderWidth: 1,
            borderRadius: 4
          }
        ]
      },
      hourlyFailedChart: {
        labels: hourlyLabels,
        datasets: [
          {
            label: 'Failed Translations',
            data: hourlyFailedData,
            backgroundColor: 'rgba(239, 68, 68, 0.8)',
            borderColor: 'rgb(239, 68, 68)',
            borderWidth: 1
          }
        ]
      }
    };
  };

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pl_prim_color_1"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <FaTimes className="text-4xl mx-auto mb-2" />
            <p className="text-lg font-semibold">Error Loading Dashboard</p>
          </div>
          <p className="text-pl_txt_light_3 mb-4">{error}</p>
          <button
            onClick={loadDashboardData}
            className="px-4 py-2 bg-pl_prim_color_1 text-white rounded-lg hover:bg-pl_prim_color_2 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-pl_txt_light_1 flex items-center gap-3">
          <FaLanguage className="text-pl_prim_color_1" />
          Translation Dashboard
        </h1>
        <p className="text-pl_txt_light_3 mt-2">
          Overview of translation activities and performance metrics
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-pl_bg_light_5 rounded-lg p-6 border-l-4 border-l-pl_prim_color_1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-pl_txt_light_3">Today's Translations</p>
              <p className="text-2xl font-bold text-pl_txt_light_1">{stats.totalTranslations.toLocaleString()}</p>
            </div>
            <FaLanguage className="text-3xl text-pl_prim_color_1" />
          </div>
        </div>

        <div className="bg-pl_bg_light_5 rounded-lg p-6 border-l-4 border-l-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-pl_txt_light_3">Failed</p>
              <p className="text-2xl font-bold text-pl_txt_light_1">{stats.failedTranslations.toLocaleString()}</p>
            </div>
            <FaExclamationTriangle className="text-3xl text-red-500" />
          </div>
        </div>
      </div>

      {/* Additional Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-pl_bg_light_5 rounded-lg p-6 border-l-4 border-l-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-pl_txt_light_3">Last 7 Days</p>
              <p className="text-2xl font-bold text-pl_txt_light_1">{stats.recent7DaysCount.toLocaleString()}</p>
            </div>
            <FaGlobe className="text-3xl text-blue-500" />
          </div>
        </div>

        <div className="bg-pl_bg_light_5 rounded-lg p-6 border-l-4 border-l-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-pl_txt_light_3">This Month</p>
              <p className="text-2xl font-bold text-pl_txt_light_1">{stats.monthlyCount.toLocaleString()}</p>
            </div>
            <FaChartBar className="text-3xl text-purple-500" />
          </div>
        </div>

        <div className="bg-pl_bg_light_5 rounded-lg p-6 border-l-4 border-l-indigo-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-pl_txt_light_3">Language Pairs</p>
              <p className="text-2xl font-bold text-pl_txt_light_1">{stats.languagePairStats.length}</p>
            </div>
            <FaLanguage className="text-3xl text-indigo-500" />
          </div>
        </div>
      </div>

      {/* Charts and Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Analytics Overview */}
        <div className="bg-pl_bg_light_5 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-pl_txt_light_1 mb-4 flex items-center gap-2">
            <FaChartBar />
            Overall Analytics
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-pl_txt_light_1">Total Translations</span>
              <span className="text-sm text-pl_txt_light_3">{analytics.totalTranslates.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-pl_txt_light_1">Successful</span>
              <span className="text-sm text-green-600">{analytics.totalSuccess.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-pl_txt_light_1">Failed</span>
              <span className="text-sm text-red-600">{analytics.totalFailed.toLocaleString()}</span>
            </div>
            <div className="w-full bg-pl_border_light_1 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-green-500 to-pl_prim_color_1 h-2 rounded-full"
                style={{ width: `${analytics.totalTranslates > 0 ? (analytics.totalSuccess / analytics.totalTranslates * 100) : 0}%` }}
              ></div>
            </div>
            <p className="text-xs text-pl_txt_light_3 text-center">
              Success Rate: {analytics.totalTranslates > 0 ? ((analytics.totalSuccess / analytics.totalTranslates) * 100).toFixed(1) : 0}%
            </p>
          </div>
        </div>

        {/* Language Pair Distribution Chart */}
        <div className="bg-pl_bg_light_5 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-pl_txt_light_1 mb-4 flex items-center gap-2">
            <FaLanguage />
            Top Language Pairs
          </h3>
          {stats.languagePairStats.length === 0 ? (
            <div className="text-center text-pl_txt_light_3 py-8">
              No language pair data available
            </div>
          ) : (
            <div className="space-y-3">
              {stats.languagePairStats.slice(0, 5).map((pair, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-6 h-6 bg-pl_prim_color_1 rounded-full flex items-center justify-center text-white text-xs font-bold">
                      {index + 1}
                    </div>
                    <span className="text-sm font-medium text-pl_txt_light_1 truncate">
                      {pair.pair}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-pl_txt_light_3">{pair.count}</span>
                    <div className="w-20 bg-pl_border_light_1 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-pl_prim_color_1 to-pl_prim_color_2 h-2 rounded-full"
                        style={{ width: `${stats.languagePairStats[0].count > 0 ? (pair.count / stats.languagePairStats[0].count) * 100 : 0}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
              {stats.languagePairStats.length > 5 && (
                <div className="text-center pt-2">
                  <span className="text-xs text-pl_txt_light_3">
                    +{stats.languagePairStats.length - 5} more language pairs
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 7-Day and 30-Day Bar Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* 7-Day Bar Chart */}
        <div className="bg-pl_bg_light_5 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-pl_txt_light_1 mb-4 flex items-center gap-2">
            <FaChartBar />
            7 Days Translation Activity
          </h3>
          {chartData.sevenDayBarChart ? (
            <div className="h-64">
              <Bar
                data={chartData.sevenDayBarChart}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false
                    },
                    tooltip: {
                      mode: 'index',
                      intersect: false,
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        color: '#6B7280'
                      },
                      grid: {
                        color: '#374151'
                      }
                    },
                    x: {
                      ticks: {
                        color: '#6B7280'
                      },
                        grid: {
                        color: '#374151'
                      }
                    }
                  }
                }}
              />
            </div>
          ) : (
            <div className="text-center text-pl_txt_light_3 py-8">
              Loading chart data...
            </div>
          )}
        </div>

        {/* 30-Day Bar Chart */}
        <div className="bg-pl_bg_light_5 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-pl_txt_light_1 mb-4 flex items-center gap-2">
            <FaChartBar />
            30 Days Translation Activity
          </h3>
          {chartData.thirtyDayBarChart ? (
            <div className="h-64">
              <Bar
                data={chartData.thirtyDayBarChart}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false
                    },
                    tooltip: {
                      mode: 'index',
                      intersect: false,
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        color: '#6B7280'
                      },
                      grid: {
                        color: '#374151'
                      }
                    },
                    x: {
                      ticks: {
                        color: '#6B7280'
                      },
                      grid: {
                        color: '#374151'
                      }
                    }
                  }
                }}
              />
            </div>
          ) : (
            <div className="text-center text-pl_txt_light_3 py-8">
              Loading chart data...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TranslateDashboard; 